import React from 'react';
import { Thermometer, Activity, Shield, LineChart } from 'lucide-react';

const Workflow: React.FC = () => {
  const steps = [
    {
      number: "01",
      title: "Environmental Parameter Monitoring",
      description: "Our comprehensive system monitors critical environmental parameters including temperature, humidity, air pressure, and air quality across your sterile processing departments and operating rooms.",
      image: "https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg",
      alt: "Hospital monitoring system",
      benefits: [
        "Real-time temperature monitoring across all areas",
        "Continuous humidity level tracking and control",
        "Differential air pressure monitoring for clean rooms"
      ]
    },
    {
      number: "02",
      title: "Automated Data Collection & Analysis",
      description: "Automated sensors continuously collect and analyze environmental data, providing instant insights and maintaining optimal conditions for sterile processing.",
      image: "https://images.pexels.com/photos/7659564/pexels-photo-7659564.jpeg",
      alt: "Healthcare monitoring dashboard",
      benefits: [
        "24/7 automated environmental monitoring",
        "Real-time data analysis and trending",
        "Customizable reporting and analytics"
      ]
    },
    {
      number: "03",
      title: "Intelligent Alert System",
      description: "Advanced alert system immediately notifies staff of any environmental parameter deviations, ensuring quick response to maintain sterile conditions.",
      image: "https://images.pexels.com/photos/7089630/pexels-photo-7089630.jpeg",
      alt: "Healthcare compliance monitoring",
      benefits: [
        "Instant alerts for parameter deviations",
        "Customizable alert thresholds",
        "Mobile notifications for staff"
      ]
    },
    {
      number: "04",
      title: "Compliance & Documentation",
      description: "Automated documentation and reporting ensure compliance with regulatory standards while providing comprehensive environmental control records.",
      image: "https://images.pexels.com/photos/8460157/pexels-photo-8460157.jpeg",
      alt: "Healthcare facility optimization",
      benefits: [
        "Automated compliance documentation",
        "Historical data archiving",
        "Regulatory standard adherence"
      ]
    }
  ];

  return (
    <section id="workflow" className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Intelligent Hospital Environment Management</h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-lg">
            Our advanced monitoring system ensures optimal environmental conditions in Sterile Processing Departments and Operating Rooms through automated monitoring of temperature, humidity, air pressure, and other critical parameters.
          </p>
        </div>

        <div className="space-y-20">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-10`}
            >
              <div className="lg:w-1/2">
                <div className="relative">
                  <div className="absolute -left-4 -top-4 w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-r from-[#2196F3] to-[#4CAF50] text-white font-bold">
                    {step.number}
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <img 
                      src={step.image} 
                      alt={step.alt} 
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
              
              <div className="lg:w-1/2">
                <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-600 text-lg mb-6">{step.description}</p>
                
                <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-[#4CAF50]">
                  <h4 className="font-medium text-[#2196F3] mb-2">Key Features</h4>
                  <ul className="space-y-2">
                    {step.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#4CAF50] mr-2">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 bg-gradient-to-r from-[#2196F3] to-[#4CAF50] rounded-2xl overflow-hidden shadow-xl">
          <div className="p-10 lg:p-16 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-6">Environmental Control Excellence</h3>
            <p className="text-white/90 text-lg mb-8">
              Our automated environmental monitoring system ensures optimal conditions in your Sterile Processing Department and Operating Rooms, maintaining the highest standards of sterility and safety while reducing manual monitoring burden on staff.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Temperature Control</h4>
                <p className="text-white/80">Precise temperature monitoring and control for optimal sterile conditions</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Humidity Management</h4>
                <p className="text-white/80">Continuous humidity monitoring to prevent microbial growth</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Air Pressure</h4>
                <p className="text-white/80">Differential pressure monitoring for clean room compliance</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Air Quality</h4>
                <p className="text-white/80">Continuous monitoring of air particulates and quality</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Workflow;